<template>
  <div class="flex w-full flex-col items-center justify-center">
    <p v-if="isLoadedInIframe()" class="font-medium text-gray-700">
      Security alert: The content is loaded through iframe which may not be secure. Please login
      from
      <a class="text-blue-600" href="https://app.agentnoon.com">https://app.agentnoon.com</a>
    </p>

    <template v-else>
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <div class="flex flex-col justify-start">
          <h2 class="mt-6 pb-3 text-3xl font-bold text-gray-900">
            {{ usesPassword || usesGoogle ? 'Log in to your account' : "Let's get started" }}
          </h2>
          <h3 class="text-gray-500">
            {{
              usesPassword || usesGoogle
                ? 'Welcome back! Please enter your details'
                : 'Please enter your details.'
            }}
          </h3>
        </div>
      </div>

      <div class="mx-auto mt-8 w-full max-w-md">
        <div class="space-y-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <div class="mt-1">
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                placeholder="Enter your email"
                autocomplete="email"
                required
                class="block h-10 w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm"
                :disabled="usesGoogle || usesMicrosoft || usesPassword"
                @keyup.enter="handleSubmit"
              />
            </div>

            <div v-if="usesPassword" class="mt-4 flex w-full flex-col">
              <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
              <div class="relative mt-1">
                <input
                  id="password"
                  ref="passwordInputRef"
                  v-model="password"
                  name="password"
                  :type="viewPassword ? 'text' : 'password'"
                  placeholder="Enter your password"
                  autocomplete="password"
                  required
                  autofocus
                  class="block h-10 w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 disabled:bg-gray-50 sm:text-sm"
                  @keyup.enter="handleUsePassword"
                />
                <div class="absolute right-2 top-2">
                  <button type="button" tabindex="1" @click="togglePasswordView">
                    <EyeOpenIcon v-if="viewPassword" class="h-4 w-4" />
                    <EyeClosedIcon v-else class="h-4 w-4" />
                  </button>
                </div>
              </div>

              <button
                class="ml-auto mt-1 text-sm text-blue-700 hover:text-blue-900"
                tabindex="1"
                @click="sendPassResetEmail"
              >
                Forgot password
              </button>
            </div>
          </div>

          <div v-if="errorMsg" class="rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="shrink-0">
                <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">
                  {{ errorMsg }}
                </h3>
              </div>
            </div>
          </div>
          <div v-if="successMsg" class="rounded-mb bg-green-50 p-4">
            <div class="flex">
              <div class="shrink-0">
                <CheckCircleIcon class="h-5 w-5 text-green-500" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-green-600">
                  {{ successMsg }}
                </h3>
              </div>
            </div>
          </div>

          <BaseButton
            v-if="!usesPassword && !usesGoogle && !usesMicrosoft && !usesOkta && !usesSaml"
            :disabled="isAuthenticating"
            class="h-11 w-full"
            @click="handleSubmit"
          >
            {{ 'Next' }}
            <SpinnerComp v-if="isAuthenticating" />
          </BaseButton>

          <BaseButton
            v-if="usesPassword"
            id="login-button"
            :disabled="isAuthenticating"
            tabindex="0"
            class="h-11 w-full"
            :is-loading="isAuthenticating"
            @click="handleUsePassword"
          >
            {{ usesPassword ? 'Login with password' : 'Next' }}
          </BaseButton>

          <div v-if="usesPassword" class="text-center text-sm uppercase text-gray-500">Or</div>

          <BaseButton
            v-if="usesGoogle"
            :disabled="isAuthenticating"
            class="h-11 w-full"
            tabindex="0"
            color="white"
            @click="handleUseGoogle"
          >
            <GoogleIcon class="h-5 w-5" />
            <p class="ml-2">Log in with Google Workspace</p>
          </BaseButton>

          <BaseButton
            v-if="!usesOkta && (usesMicrosoft || usesPassword)"
            :disabled="isAuthenticating"
            class="h-11 w-full"
            tabindex="0"
            color="white"
            @click="handleUseMicrosoft"
          >
            <MicrosoftLogo class="mr-2 h-5 w-5" />
            Log in with Microsoft
          </BaseButton>

          <BaseButton
            v-if="usesOkta || (usesPassword && oktaProvider)"
            :disabled="isAuthenticating"
            color="white"
            class="h-11 w-full"
            tabindex="0"
            @click="oktaAuth"
          >
            <img :src="OktaIcon" class="h-5 w-auto" />

            Log in with Okta
          </BaseButton>

          <BaseButton
            v-if="usesSaml"
            :disabled="isAuthenticating"
            color="white"
            class="h-11 w-full"
            tabindex="0"
            @click="samlAuth"
          >
            <img :src="OktaIcon" class="h-5 w-auto" />

            Log in with Okta
          </BaseButton>

          <BaseButton
            v-if="usesFirebaseSaml"
            :disabled="isAuthenticating"
            color="white"
            class="h-11 w-full"
            tabindex="0"
            @click="samlFirebaseAuth"
          >
            Log in with SAML
          </BaseButton>
        </div>

        <div class="pt-12 text-center text-blue-700 hover:text-blue-900">
          <BaseButton
            v-if="usesPassword || usesGoogle"
            tabindex="5"
            class="w-full"
            color="blue-transparent"
            @click="resetDetails"
          >
            Use a different email address
          </BaseButton>
        </div>
      </div>
    </template>
  </div>

  <ToastDisplay />
</template>

<script setup>
import MicrosoftLogo from '@/assets/microsoft.svg'
import {
  checkAccountStatus,
  failedLoginAttempt,
  resetPassword,
  isMFAEnabled,
  sendLoginOTP
} from '@/services/account-api.service.js'
import { CheckCircleIcon } from '@heroicons/vue/outline'
import { XCircleIcon } from '@heroicons/vue/solid'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import EyeClosedIcon from '@/assets/SvgIcons/EyeClosed.svg'
import EyeOpenIcon from '@/assets/SvgIcons/EyeOpen.svg'
import GoogleIcon from '@/assets/SvgIcons/GoogleIcon.svg'
import { OktaMapping, SamlProviders, FirebaseSamlProviders } from '@/lib/Okta'
import OktaIcon from '@/assets/okta.png'
import { computed, ref, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { getAuth, sendPasswordResetEmail } from '@firebase/auth'
import useToast from '@/hooks/use-toast'
import ToastDisplay from '@/components/ToastDisplay.vue'
import * as Sentry from '@sentry/browser'
import { AUTH_PROVIDERS } from '@/lib/AuthProviders'
import { isFirewallError } from '@/services/api.service'
import SpinnerComp from '@/components/SpinnerComp.vue'
import BaseButton from '@/components/BaseButton.vue'

const router = useRouter()
const route = useRoute()
const { dispatch } = useStore()
const { queueErrorToast } = useToast()

const email = ref('')
const password = ref('')
const errorMsg = ref('')
const successMsg = ref('')
const isAuthenticating = ref(false)
const usesPassword = ref(false)
const usesGoogle = ref(false)
const usesMicrosoft = ref(false)
const usesOkta = ref(false)
const usesSaml = ref(false)
const usesFirebaseSaml = ref(false)
const viewPassword = ref(false)
const provider = ref(null)
const loginRequired = ref(false)
const passwordInputRef = ref(null)

function isLoadedInIframe() {
  return window.location !== window.parent.location
}

/**
 * Makes a call to firebase, if User exists, returns the User's provider.
 * If User does not exist, redirects them to the Signup page.
 * User status will be:
 * - "password" if User logs in with email & password,
 * - "google.com" if User logs in with Google OAuth,
 * - "none" if User does not exist.
 * @returns { null }
 */
async function checkUserExists() {
  errorMsg.value = ''

  // We already have the "required" attribute on the input, but will leave this here just in case.
  if (email.value.trim() === '') {
    errorMsg.value = 'Please enter an email'
    return
  }

  const blockedDomains = []
  const domain = email.value.split('@')[1]
  if (blockedDomains.includes(domain)) {
    errorMsg.value = 'Please use your work email'
    return
  }

  try {
    isAuthenticating.value = true

    // Make a call to firebase function that returns User provider status
    const response = await checkAccountStatus(email.value)
    provider.value = response.data.status
    const allProviders = response.data.providers || []

    allProviders.forEach((_provider) => {
      if (_provider === AUTH_PROVIDERS.PASSWORD) usesPassword.value = true
      if (_provider === AUTH_PROVIDERS.MICROSOFT) usesMicrosoft.value = true
      if (_provider === AUTH_PROVIDERS.GOOGLE) usesGoogle.value = true
    })

    switch (provider.value) {
      case AUTH_PROVIDERS.PASSWORD:
        usesPassword.value = true
        break
      case AUTH_PROVIDERS.GOOGLE:
        usesGoogle.value = true
        break
      case AUTH_PROVIDERS.MICROSOFT:
        usesMicrosoft.value = true
        break
      default: {
        if (
          SamlProviders.includes(provider.value) ||
          allProviders.some((_provider) => SamlProviders.includes(_provider))
        ) {
          usesSaml.value = true

          if (!SamlProviders.includes(provider.value)) {
            provider.value = allProviders.find((_provider) => SamlProviders.includes(_provider))
          }
        } else if (FirebaseSamlProviders.includes(provider.value)) {
          usesFirebaseSaml.value = true
        } else {
          const oktaProvider = Object.values(OktaMapping).find((_oktaProvider) => {
            return provider.value === _oktaProvider
          })

          if (oktaProvider) {
            usesOkta.value = true

            //generating spahgetti code here - in case of okta, we want to check if email / pass has been already setup
            if (allProviders.includes('password')) {
              usesPassword.value = true
            }
          } else {
            router.push({ name: 'SignUp', query: { email: email.value } })
          }
        }
      }
    }
  } catch (e) {
    console.error(e)
    const isReachable = await isFirewallError(e)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
    else {
      errorMsg.value = 'Something went wrong while processing your info!'
    }
  } finally {
    isAuthenticating.value = false
  }
}

// The flow here is as such:
// User enters email -> handleSubmit runs this.checkUserExists() -> If User exists, then:
// If User uses password login -> Render the password login form, set this.usesPassword to true
// If User uses google login -> Render the google login form, set this.usesGoogle to true
// If User doesn't exist -> Redirect them to /signup
async function handleSubmit() {
  if (usesPassword.value) {
    await toLogin()
  } else if (usesGoogle.value) {
    await googleAuth()
  } else if (usesMicrosoft.value) {
    await microsoftAuth()
  } else if (usesOkta.value) {
    await oktaAuth()
  } else if (usesSaml.value) {
    await samlAuth()
  } else if (usesFirebaseSaml.value) {
    await samlFirebaseAuth()
  } else {
    checkUserExists()
  }
}

async function handleUsePassword() {
  if (usesPassword.value) {
    await toLogin()
  } else {
    checkUserExists()
  }
}

async function handleUseGoogle() {
  if (usesGoogle.value) {
    await googleAuth()
  } else {
    checkUserExists()
  }
}

async function handleUseMicrosoft() {
  usesMicrosoft.value = true
  usesPassword.value = false
  await microsoftAuth()
}

function resetDetails() {
  email.value = ''
  password.value = ''
  usesGoogle.value = false
  usesPassword.value = false
  usesMicrosoft.value = false
  usesOkta.value = false
  usesSaml.value = false
  usesFirebaseSaml.value = false
  errorMsg.value = ''
  successMsg.value = ''
  provider.value = null
  loginRequired.value = false
  isAuthenticating.value = false
}

async function toLogin() {
  isAuthenticating.value = true

  try {
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
    await firebase.auth().signInWithEmailAndPassword(email.value, password.value)

    await handleMFA()
  } catch (error) {
    Sentry?.captureException(error)
    console.error('Login Error:', error)
    const response = await failedLoginAttempt(email.value)
    errorMsg.value = response.data.message
    isAuthenticating.value = false
    const res = await isFirewallError(error)
    if (!res) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

function togglePasswordView() {
  viewPassword.value = !viewPassword.value
}

async function googleAuth() {
  try {
    isAuthenticating.value = true
    const success = await dispatch('googleSignIn')

    if (success) {
      await handleMFA()
    } else {
      errorMsg.value = 'Something went wrong while signing in with Google'
    }

    isAuthenticating.value = false
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

async function microsoftAuth() {
  try {
    isAuthenticating.value = true
    const success = await dispatch('microsoftSignIn')

    if (success) {
      await handleMFA()
    } else {
      errorMsg.value = 'Something went wrong while signing in with Microsoft'
    }

    isAuthenticating.value = false
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

async function oktaAuth() {
  try {
    isAuthenticating.value = true

    const oktaEnabledDomain = Object.keys(OktaMapping).find((domain) => {
      return email.value?.includes(domain)
    })

    const oktaProvider = OktaMapping[oktaEnabledDomain]
    const success = await dispatch('oktaSignIn', { providerId: oktaProvider })

    if (success) {
      await handleMFA()
    } else {
      errorMsg.value = 'Something went wrong while signing in with Okta'
    }

    isAuthenticating.value = false
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

async function samlFirebaseAuth() {
  try {
    isAuthenticating.value = true

    const success = await dispatch('firebaseSamlSignIn', {
      providerId: provider.value
    })

    if (success) {
      await handleMFA()
    } else {
      errorMsg.value = 'Something went wrong while signing in with Okta'
    }

    isAuthenticating.value = false
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

async function samlAuth() {
  try {
    isAuthenticating.value = true

    const success = await dispatch('samlSignIn', {
      providerId: provider.value
    })

    if (success) {
      await handleMFA()
    } else {
      errorMsg.value = 'Something went wrong while signing in with Okta'
    }

    isAuthenticating.value = false
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}

async function sendPassResetEmail() {
  const auth = getAuth()
  try {
    const { data } = await resetPassword(email.value)

    if (data.status === 'error') {
      errorMsg.value = data.message
      return
    }

    await sendPasswordResetEmail(auth, email.value)
    successMsg.value = 'Please check your inbox to reset your password'
  } catch (err) {
    console.error(err)
    const isReachable = await isFirewallError(err)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
    else {
      errorMsg.value = 'Something went wrong while sending the email'
    }
  }
}

async function handleMFA() {
  try {
    // check if MFA is enabled for this user
    const idTokenResult = await firebase.auth().currentUser.getIdTokenResult()

    // Check if MFA is required for this user
    const MFARequired = await isMFAEnabled()

    if (MFARequired && !idTokenResult.claims.mfaVerified) {
      const response = await sendLoginOTP()
      if (response.status) {
      router.push({ name: 'MFA' })
      } else {
        queueErrorToast(response.message)
        isAuthenticating.value = false
      }
      return
    }

    redirectToApp()
  } catch (error) {
    const isReachable = await isFirewallError(error)
    if (!isReachable) {
      errorMsg.value =
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.'
    }
  }
}
const redirectToApp = () => {
  // resest Inactivity
  localStorage.setItem('showInactivityOverlay', false)
  localStorage.setItem('sessionExpired', false)
  //check for redirect query
  const redirect = route.query.redirect
  if (redirect) {
    router.push(redirect)
  } else {
    router.push({ name: 'Landing' })
  }
}

const oktaProvider = computed(() => {
  //based on the email, check if the user can access Okta through one of preconditioned providers
  const oktaEnabledDomain = Object.keys(OktaMapping).find((domain) => {
    return email.value.includes(domain)
  })

  return oktaEnabledDomain ? OktaMapping[oktaEnabledDomain] : null
})

watch(usesPassword, async (newValue) => {
  if (newValue) {
    // Wait for DOM update then focus the password field
    await nextTick()
    passwordInputRef.value?.focus()
  }
})
</script>
